import { useState } from "react";
import { useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { Button, Grid, styled, Typography } from "@mui/material";
import * as _ from "lodash";
import { AppDispatch, RootState } from "../../state/store"
import {
  COUNTRY,
  ERROR_MESSAGE,
  INSTRUCTIONS,
  US_STATE,
} from "../../utils/constantTypes";
import { validateEmail, validateMobile, validateZipcode } from "../../utils/validation";
import TextInput from "../TextField";
import BasicSelect from "../../components/BasicSelect"
import { createPracticeAsync, loginAsync } from "../../state/auth/authSlice";
import PhoneInputElement from "../PhoneInput";
import { CustomStyledContainer, RequiredField } from "../../content/auth/signup"
import FieldIconContainer from "../FieldIconContainer";
import { IMAGE_PATHS } from "../../utils/imagePaths";
import { iconContainerStyle, iconStyle } from "../../content/auth/login";
import { encryptData } from "../../utils";

interface Props {
  navigation?: any;
  onPressContinue: Function;
  scrollEnable?: boolean;
  setIsLoading?: any;
  fromScreen?: string;
  userID?: string;
  enableSaveButton?: any;
  setEnableSaveButton?: any
}
interface TextInputProps {
  labelImageName?: string;
  label?: any;
  labelStyles?: object;
  value: string;
  onChangeText: (text: string) => void;
  onIconClick?: () => void;
  onFocus?: () => void;
  marginBottom?: number;
  width?: number;
  secureTextEnable?: boolean;
  iconName?: string;
  iconColor?: string;
  inputContainerStyle?: object;
  validateErrormsg?: string;
  validateError?: boolean;
  editable?: boolean;
  keyboardType?: string;
  IconStyle?: object;
  dropDownInput?: boolean;
  multiline?: boolean;
  letterSpacing?: boolean;
  testID?: string;
  accessibilityLabel?: string;
  icon?: any
  placeholder?: string;
  dropdownListArr?: any[]
  type?: string;
  required?: boolean;
  instruction?: string | React.ReactNode;
}

const StyledButton = styled(Button)(({ theme }) => ({
  // margin: `${theme.spacing(2)} 0px`,
  borderRadius: '60px',
  color: '#081B22',
  background: 'linear-gradient(97.57deg, #89E141 -27.78%, #29E6CE 112.55%)',
}));
const parts = INSTRUCTIONS.PRACTICE_COMMON_EMAIL.split('After Care Summary');
const PracticeEmailInstruction = <>{parts[0]}<strong>After Care Summary</strong>{parts[1]}</>

const PracticeForm = (props) => {
  const { userId } = useSelector((state: RootState) => state.auth);
  const { email: userEmail, firstName: userFirstName, password } = useSelector((state: RootState) => state.auth.newUserPayload)
  const [countryCode, setCountryCode] = useState("+1");
  const [firstName, setFirstName] = useState("");
  const [firstNameError, setFirstNameError] = useState("");
  const [mobileNoError, setMobileNoError] = useState("");
  const [mobileNo, setMobileNo] = useState("");
  const [mobileError, showMobileError] = useState(false);
  const [addressOne, setAddressOne] = useState("");
  const [addressOneError, setAddressOneError] = useState("");
  const [addressTwo, setAddressTwo] = useState("");
  const [addressTwoError, setAddressTwoError] = useState("");
  const [suite, setSuite] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [cityError, setCityError] = useState("");
  const [selectedState, setState] = useState("California");
  const [stateError, setStateError] = useState("");
  const [selectedCountry, setCountry] = useState("United States");
  const [countryError, setCountryError] = useState("");
  const [zipCode, setZipCodde] = useState("");
  const [zipCodeError, setZipCoddeError] = useState("");
  const [usStateList, setUsStateList] = useState(
    JSON.parse(JSON.stringify(US_STATE)));
  const [countryList, setCountryList] = useState(
    JSON.parse(JSON.stringify(COUNTRY)));
  const [emailError, setEmailError] = useState("");
  const [email, setEmail] = useState("");

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const validateField = async () => {
    if (!firstName) {
      setFirstNameError(ERROR_MESSAGE.nameRequiredMsg);
    }
    if (!addressOne) {
      setAddressOneError(ERROR_MESSAGE.addressOneRequired);
    }
    if (!selectedCity) {
      setCityError(ERROR_MESSAGE.cityRequired);
    }
    if (!selectedState) {
      setStateError(ERROR_MESSAGE.stateRequired);
    }
    if (!zipCode) {
      setZipCoddeError(ERROR_MESSAGE.zipCodeRequired);
    }
    if (!selectedCountry) {
      setCountryError(ERROR_MESSAGE.countryRequired);
    }
    if (mobileNo.slice(countryCode.length + 1).length === 0) {
      showMobileError(true);
      setMobileNoError(ERROR_MESSAGE.phoneRequiredMsg);
    }
    else if (checkNoError()) {
      const _loginFrom = encryptData(process.env.REACT_APP_LOGIN_FROM);
      const res = await dispatch(
        createPracticeAsync({
          practiceInfo: {
            name: firstName,
            address1: addressOne,
            address2: addressTwo,
            suite,
            city: selectedCity,
            state: selectedState,
            zipCode: zipCode,
            country: selectedCountry,
            isVerified: true,
            officePhoneNumber: {
              countryCode: `+${countryCode}`,
              number: mobileNo.slice(countryCode.length + 1)
            },
            practiceCommonEmail: email
          },
          userId,
          userFirstName,
          userEmail
        })
      );
      if (!_.get(res, 'error', null)) {
        await dispatch(
          loginAsync({ username: userEmail, password, loginFrom: _loginFrom })
        );
        navigate('/accounts/profile');
      }
    }
  };

  const checkNoError = () => {
    let status = true;
    if (
      !firstName ||
      !addressOne ||
      !selectedCity ||
      !selectedState ||
      !zipCode ||
      !selectedCountry ||
      !mobileNo ||
      mobileNoError || emailError
    ) {
      status = false;
    }
    return status;
  };

  const textInputs: TextInputProps[] = [
    {
      labelImageName: "profile",
      label: "Name",
      placeholder: "Name",
      testID: "name-input",
      value: firstName,
      onChangeText: (text: string) => {
        setFirstName(text);
        setFirstNameError("");
      },
      validateError: firstNameError ? true : false,
      validateErrormsg: firstNameError,
      icon: <FieldIconContainer imgSrc={IMAGE_PATHS.PROFILE} imgStyle={iconStyle} containerStyle={iconContainerStyle} />,
      type: 'text',
      required: true
    },
    {
      labelImageName: "location",
      label: "Address Line 1",
      testID: "address-line-1-input",
      placeholder: "Address Line 1",
      value: addressOne,
      onChangeText: (text) => {
        setAddressOne(text);
        setAddressOneError("");
      },
      validateError: addressOneError ? true : false,
      validateErrormsg: addressOneError,
      icon: <FieldIconContainer imgSrc={IMAGE_PATHS.LOCATION} imgStyle={iconStyle} containerStyle={iconContainerStyle} />,
      type: 'text',
      required: true
    },
    {
      labelImageName: "location",
      label: "Address Line 2",
      testID: "address-line-2-input",
      labelStyles: { marginTop: 28, marginBottom: 8 },
      placeholder: "Address Line 2",
      value: addressTwo,
      onChangeText: (text) => {
        setAddressTwo(text);
        setAddressTwoError("");
      },
      validateError: addressTwoError ? true : false,
      validateErrormsg: addressTwoError,
      icon: <FieldIconContainer imgSrc={IMAGE_PATHS.LOCATION} imgStyle={iconStyle} containerStyle={iconContainerStyle} />,
      type: 'text'
    },
    {
      labelImageName: "location",
      label: "Suite",
      testID: "suite-input",
      accessibilityLabel: "suite-input",
      labelStyles: { marginTop: 28, marginBottom: 8 },
      placeholder: "e.g., Suite 201, Ste 105",
      value: suite,
      onChangeText: (text) => {
        setSuite(text);
      },
      icon: <FieldIconContainer imgSrc={IMAGE_PATHS.LOCATION} imgStyle={iconStyle} containerStyle={iconContainerStyle} />,
      type: 'text'
    },
    {
      labelImageName: "buildings",
      label: "City",
      testID: "buildings-input",
      placeholder: "City",
      value: selectedCity,
      onChangeText: (text) => {
        setSelectedCity(text);
        setCityError("");
      },
      validateError: cityError ? true : false,
      validateErrormsg: cityError,
      icon: <FieldIconContainer imgSrc={IMAGE_PATHS.BUILDING} imgStyle={iconStyle} containerStyle={iconContainerStyle} />,
      type: 'text',
      required: true
    },
    {
      labelImageName: "passwordCheck",
      label: "Zip Code",
      accessibilityLabel: "zipcode-input",
      testID: "zipcode-input",
      labelStyles: { marginTop: 28, marginBottom: 8 },
      placeholder: "Zip Code",
      value: zipCode,
      onChangeText: (text) => {
        if (validateZipcode(text)) {
          setZipCodde(text);
          setZipCoddeError("");
        }
      },
      inputContainerStyle: { flex: 1 },
      validateError: zipCodeError ? true : false,
      validateErrormsg: zipCodeError,
      icon: <FieldIconContainer imgSrc={IMAGE_PATHS.ZIPCODE} imgStyle={iconStyle} containerStyle={iconContainerStyle} />,
      type: 'text',
      required: true
    },
    {
      labelImageName: "globalEdit",
      label: "State",
      testID: "state-input",
      placeholder: "State",
      value: selectedState,
      onChangeText: (text) => {
        setState(text);
        setCityError("");
      },
      validateError: stateError ? true : false,
      validateErrormsg: stateError,
      dropDownInput: true,
      dropdownListArr: usStateList,
      icon: <FieldIconContainer imgSrc={IMAGE_PATHS.STATE} imgStyle={iconStyle} containerStyle={iconContainerStyle} />,
      type: 'select'
    },
    {
      labelImageName: "global",
      label: "Country",
      testID: "country-input",
      placeholder: "Country",
      value: selectedCountry,
      onChangeText: (text) => {
        setCountry(text);
        setCityError("");
      },

      validateError: countryError ? true : false,
      validateErrormsg: countryError,
      dropDownInput: true,
      dropdownListArr: countryList,
      icon: <FieldIconContainer imgSrc={IMAGE_PATHS.COUNTRY} imgStyle={iconStyle} containerStyle={iconContainerStyle} />,
      type: 'select'
    },
    {
      labelImageName: "sms",
      label: "Practice Common Email",
      placeholder: "Practice Common Email",
      testID: 'practice-email-input',
      value: email,
      validateError: emailError ? true : false,
      validateErrormsg: emailError,
      onChangeText: (text: string) => {
        validate(text, "email");
      },
      icon: <FieldIconContainer imgSrc={IMAGE_PATHS.EMAIL} imgStyle={iconStyle} containerStyle={iconContainerStyle} />,
      type: 'email',
      required: false,
      // instruction: PracticeEmailInstruction
    },
    // {
    //   label: "Office Phone Number",
    //   placeholder: "",
    //   testID: 'phonenumber-input',
    //   validateError: mobileNoError ? true : false,
    //   validateErrormsg: mobileNoError,
    //   value: mobileNo,
    //   onChangeText: (text: string) => {
    //     validate(text, "mobile");
    //   },
    //   icon: <FieldIconContainer imgSrc={IMAGE_PATHS.CALL} imgStyle={iconStyle} containerStyle={iconContainerStyle} />,
    //   type: 'tel'
    // },
  ];

  const validate = (text: string, type: string) => {
    switch (type) {
      case "mobile":
        setMobileNo(text);
        let mobileError = validateMobile(text);
        setMobileNoError(mobileError);
        break;
      case "email":
        setEmail(text);
        if (text) {
          let emailError = validateEmail(text);
          setEmailError(emailError);
        }
        else {
          setEmailError("");
        }
        break;
    }
  };

  return (
    <>
      <CustomStyledContainer
      >
        <Typography textAlign={"center"} variant={"h4"} mb={2}>Location Information</Typography>
        <Grid container>
          <Grid item xs={12} gap={1}>
            {textInputs.slice(0, 4).map(textInput => (
              <TextInput key={textInput.label} {...textInput} />
            ))}
          </Grid>
          <Grid item xs={12} gap={1}>
            {textInputs.slice(4, 6).map(textInput => (
              <TextInput key={textInput.label} {...textInput} />
            ))}
          </Grid>
          <Grid item xs={12} gap={1}>
            {textInputs.slice(6, 8).map(textInput => (
              <BasicSelect value={textInput.value} key={textInput.label} {...textInput} />
            ))}
          </Grid>
          <Grid sm={12} item pb={2}>
            <PhoneInputElement
              label={"Office Phone Number"}
              emptyError={mobileError}
              showMobileError={showMobileError}
              setCountryCode={setCountryCode}
              mobileNo={mobileNo}
              mobileNoError={mobileNoError}
              validate={validate}
            />
          </Grid>
          <Grid item xs={12} gap={1}>
            {textInputs.slice(8, 9).map(textInput => (
              <TextInput key={textInput.label} {...textInput} />
            ))}
          </Grid>
        </Grid>

        <StyledButton
          variant="contained"
          fullWidth
          onClick={validateField}
        >
          Continue
        </StyledButton>
      </CustomStyledContainer >
    </>)
}
export default PracticeForm;